import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useTableSort, sortRows } from '@/hooks/useTableSort';
import { SortableTh } from '@/components/ui/SortableTh';
import {
  Boxes,
  Search,
  CalendarDays,
  Loader2,
  AlertCircle,
  Warehouse,
  Truck,
  PackageOpen,
  RefreshCw,
  TrendingUp,
  FileSpreadsheet,
  FileDown,
  Hash,
  Package,
  Send,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { usePermissions } from '@/hooks/usePermissions';
import { fetchLastSent, type LastSent } from '@/lib/consignmentStoreReport';
import { SendConsignmentReportModal } from '@/components/consignment/SendConsignmentReportModal';
import { ConsignmentStorePickerModal, type ConsignmentStore } from '@/components/consignment/ConsignmentStorePickerModal';

// ─── Types ────────────────────────────────────────────────────────────────────

type ValuationMethod = 'CURRENT_COST' | 'FIFO' | 'LIFO';

interface BalanceRow {
  product_id:           string;
  sku:                  string | null;
  product_name:         string;
  image_url:            string | null;
  supplier_id:          string | null;
  warehouse_id:         string;
  warehouse_name:       string;
  calculated_quantity:  number;
  total_value:          number;
  is_external:          boolean;
}

interface WarehouseOption { id: string; name: string; }
interface SupplierOption  { id: string; name: string; }

interface Props {
  companyId: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatQty(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return parseFloat(n.toFixed(3)).toString();
}

function formatCurrency(n: number): string {
  return n.toLocaleString('he-IL', {
    style:                 'currency',
    currency:              'ILS',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const VALUATION_OPTIONS: { value: ValuationMethod; label: string }[] = [
  { value: 'CURRENT_COST', label: 'עלות נוכחית (Current Cost)' },
  { value: 'FIFO',         label: 'FIFO — ראשון נכנס ראשון יוצא' },
  { value: 'LIFO',         label: 'LIFO — אחרון נכנס ראשון יוצא' },
];

// ─── Export helpers ───────────────────────────────────────────────────────────

async function exportBalancesToExcel(rows: BalanceRow[], suppliers: SupplierOption[], asOfDate: string) {
  const XLSX = await import('xlsx');
  const supplierMap = new Map(suppliers.map(s => [s.id, s.name]));
  const wsData = rows.map(r => ({
    'מק\"ט': r.sku ?? '',
    'שם מוצר': r.product_name,
    'מחסן': r.warehouse_name,
    'ספק': r.supplier_id ? (supplierMap.get(r.supplier_id) ?? '') : '',
    'יתרה': r.calculated_quantity,
    'שווי מלאי': r.total_value ?? 0,
  }));
  const ws = XLSX.utils.json_to_sheet(wsData);
  ws['!cols'] = [{ wch: 14 }, { wch: 25 }, { wch: 18 }, { wch: 18 }, { wch: 10 }, { wch: 14 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'יתרות מלאי');
  XLSX.writeFile(wb, `יתרות_מלאי_${asOfDate}.xlsx`);
}

async function exportBalancesToPDF(tableEl: HTMLElement, asOfDate: string) {
  const { exportTableToPDF } = await import('../../lib/pdfExport');
  await exportTableToPDF(tableEl, `יתרות_מלאי_${asOfDate}.pdf`);
}

// ─── Component ────────────────────────────────────────────────────────────────

export function InventoryBalancesView({ companyId }: Props) {

  // ── Filter state ────────────────────────────────────────────────────────────
  const [asOfDate,        setAsOfDate]        = useState<string>(todayISO());
  const [search,          setSearch]          = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState<string>('all');
  const [supplierFilter,  setSupplierFilter]  = useState<string>('all');
  const [valuation,       setValuation]       = useState<ValuationMethod>('CURRENT_COST');

  // ── Data state ───────────────────────────────────────────────────────────────
  const [rows,       setRows]       = useState<BalanceRow[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [suppliers,  setSuppliers]  = useState<SupplierOption[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [exporting,  setExporting]  = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [lastCountDates, setLastCountDates] = useState<Record<string, string>>({});

  const tableRef = useRef<HTMLDivElement>(null);

  // ── Send balances to store ────────────────────────────────────────────────────────
  const { can } = usePermissions();
  const canSend = can({ module: 'inventory', level: 'editor' });
  const [stores,     setStores]     = useState<(ConsignmentStore & { consignment_warehouse_id: string })[]>([]);
  const [lastSent,   setLastSent]   = useState<Record<string, LastSent>>({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [sendTarget, setSendTarget] = useState<{ store: ConsignmentStore; asOf: string } | null>(null);

  const loadSendInfo = useCallback(async () => {
    if (!companyId || !canSend) return;
    const { data, error: storesErr } = await supabase
      .from('inventory_clients')
      .select('id, name, consignment_warehouse_id')
      .eq('company_id', companyId)
      .eq('allow_consignment', true)
      .not('consignment_warehouse_id', 'is', null)
      .order('name');
    if (storesErr) {
      console.error('Failed to load consignment stores:', storesErr);
      return;
    }
    setStores((data ?? []) as (ConsignmentStore & { consignment_warehouse_id: string })[]);
    try {
      setLastSent(await fetchLastSent(companyId));
    } catch (e) {
      console.error('Failed to load last-sent dates:', e);
    }
  }, [companyId, canSend]);

  useEffect(() => { loadSendInfo(); }, [loadSendInfo]);

  const openSend = () => {
    const match = warehouseFilter !== 'all'
      ? stores.find(s => s.consignment_warehouse_id === warehouseFilter)
      : undefined;
    if (match) setSendTarget({ store: { id: match.id, name: match.name }, asOf: asOfDate });
    else setPickerOpen(true);
  };

  const pickStore = (s: ConsignmentStore) => {
    setPickerOpen(false);
    setSendTarget(prev => ({ store: { id: s.id, name: s.name }, asOf: prev?.asOf ?? asOfDate }));
  };

  // ── Load dropdown options once ───────────────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    Promise.all([
      supabase.from('inventory_warehouses').select('id, name')
        .eq('company_id', companyId).eq('is_external', false).order('name'),
      supabase.from('inventory_suppliers').select('id, name')
        .eq('company_id', companyId).order('name'),
    ]).then(([wRes, sRes]) => {
      if (wRes.data) setWarehouses(wRes.data as WarehouseOption[]);
      if (sRes.data) setSuppliers(sRes.data as SupplierOption[]);
    });
  }, [companyId]);

  // ── Fetch via RPC ───────────────────────────────────────────────────────────
  const fetchBalances = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);

    const { data, error: rpcError } = await supabase.rpc(
      'get_inventory_balances_as_of',
      {
        p_company_id:       companyId,
        p_as_of_date:       asOfDate,
        p_valuation_method: valuation,
        p_warehouse_id:     warehouseFilter !== 'all' ? warehouseFilter : null,
      }
    ).limit(10000);

    setLoading(false);

    if (rpcError) {
      console.error('[InventoryBalancesView] RPC error:', rpcError);
      setError(`שגיאה בטעינת יתרות: ${rpcError.message}`);
      setRows([]);
      return;
    }

    setRows((data as BalanceRow[]) ?? []);
  }, [companyId, asOfDate, valuation, warehouseFilter]);

  useEffect(() => { fetchBalances(); }, [fetchBalances]);

  // ── Load last stock count dates ──────────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    supabase.rpc('get_last_stock_count_dates', { p_company_id: companyId })
      .then(({ data }) => {
        if (!data) return;
        const map: Record<string, string> = {};
        (data as Array<{ product_id: string; warehouse_id: string; last_count_date: string }>)
          .forEach(r => { map[`${r.product_id}::${r.warehouse_id}`] = r.last_count_date; });
        setLastCountDates(map);
      });
  }, [companyId]);

  // ── Supplier name lookup ────────────────────────────────────────────────────
  const supplierMap = useMemo(() => {
    const m: Record<string, string> = {};
    suppliers.forEach(s => { m[s.id] = s.name; });
    return m;
  }, [suppliers]);

  const { sort, toggle } = useTableSort<string>('product_name', 'asc');

  // ── Local filtering ─────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = rows.filter(r => {
      if (q) {
        const nameMatch = r.product_name.toLowerCase().includes(q);
        const skuMatch  = (r.sku ?? '').toLowerCase().includes(q);
        if (!nameMatch && !skuMatch) return false;
      }
      if (warehouseFilter !== 'all' && r.warehouse_id !== warehouseFilter) return false;
      if (supplierFilter  !== 'all' && r.supplier_id  !== supplierFilter)  return false;
      return true;
    });
    if (sort.key === 'supplier_name') {
      return sortRows(base.map(r => ({ ...r, _sup: r.supplier_id ? (supplierMap[r.supplier_id] ?? '') : '' })), '_sup', sort.dir).map(({ _sup, ...rest }) => rest as typeof base[number]);
    }
    if (sort.key === 'last_count') {
      return sortRows(base.map(r => ({ ...r, _lcd: lastCountDates[`${r.product_id}::${r.warehouse_id}`] ?? '' })), '_lcd', sort.dir).map(({ _lcd, ...rest }) => rest as typeof base[number]);
    }
    return sortRows(base, sort.key, sort.dir);
  }, [rows, search, warehouseFilter, supplierFilter, sort, supplierMap, lastCountDates]);

  // ── Aggregates ─────────────────────────────────────────────────────────────
  const { totalSkus, totalQty, totalValue } = useMemo(() => {
    const skuSet = new Set<string>();
    let qty = 0;
    let val = 0;
    for (const r of filtered) {
      skuSet.add(r.product_id);
      qty += r.calculated_quantity;
      val += r.total_value ?? 0;
    }
    return { totalSkus: skuSet.size, totalQty: qty, totalValue: val };
  }, [filtered]);

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-5" dir="rtl">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-xl">
            <Boxes className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">יתרות מלאי</h2>
            <p className="text-xs text-gray-500">
              יתרה ושווי מלאי לפי מוצר ומחסן נכון לתאריך הנבחר
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {canSend && stores.length > 0 && (
            <button
              onClick={openSend}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-sm font-semibold text-white hover:bg-blue-700 shadow-sm transition-colors"
            >
              <Send className="w-4 h-4" />
              שלח יתרות לחנות
            </button>
          )}
          <button
            onClick={async () => {
              setExporting(true);
              try { await exportBalancesToExcel(filtered, suppliers, asOfDate); } catch { /* ignore */ } finally { setExporting(false); }
            }}
            disabled={exporting || filtered.length === 0}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-sm text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 transition-colors"
          >
            <FileSpreadsheet className="w-4 h-4" />
            אקסל
          </button>
          <button
            onClick={async () => {
              if (!tableRef.current) return;
              setExporting(true);
              try { await exportBalancesToPDF(tableRef.current, asOfDate); } catch (e) { console.error(e); alert('שגיאה ביצירת PDF'); } finally { setExporting(false); }
            }}
            disabled={exporting || filtered.length === 0}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-blue-200 bg-blue-50 text-sm text-blue-700 hover:bg-blue-100 disabled:opacity-50 transition-colors"
          >
            <FileDown className="w-4 h-4" />
            PDF
          </button>
        </div>
      </div>

      {/* ── Filter toolbar ──────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-3 items-end">

        {/* As-of date */}
        <div className="flex flex-col gap-1 min-w-[160px]">
          <label className="text-xs text-gray-500 flex items-center gap-1">
            <CalendarDays className="w-3 h-3" />
            נכון לתאריך
          </label>
          <input
            type="date"
            value={asOfDate}
            onChange={e => setAsOfDate(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-blue-400
                       focus:border-transparent"
          />
        </div>

        {/* Valuation method */}
        <div className="flex flex-col gap-1 min-w-[220px]">
          <label className="text-xs text-gray-500 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            שיטת תמחור
          </label>
          <select
            value={valuation}
            onChange={e => setValuation(e.target.value as ValuationMethod)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-blue-400
                       focus:border-transparent bg-white"
          >
            {VALUATION_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Search */}
        <div className="flex flex-col gap-1 flex-1 min-w-[180px]">
          <label className="text-xs text-gray-500 flex items-center gap-1">
            <Search className="w-3 h-3" />
            חיפוש מוצר
          </label>
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4
                               text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="שם מוצר או מקט…"
              className="w-full pr-9 pl-3 py-2 border border-gray-200 rounded-lg text-sm
                         focus:outline-none focus:ring-2 focus:ring-blue-400
                         focus:border-transparent"
            />
          </div>
        </div>

        {/* Warehouse filter */}
        <div className="flex flex-col gap-1 min-w-[160px]">
          <label className="text-xs text-gray-500 flex items-center gap-1">
            <Warehouse className="w-3 h-3" />
            מחסן
          </label>
          <select
            value={warehouseFilter}
            onChange={e => setWarehouseFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-blue-400
                       focus:border-transparent bg-white"
          >
            <option value="all">כל המחסנים</option>
            {warehouses.map(w => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </div>

        {/* Supplier filter */}
        <div className="flex flex-col gap-1 min-w-[160px]">
          <label className="text-xs text-gray-500 flex items-center gap-1">
            <Truck className="w-3 h-3" />
            ספק
          </label>
          <select
            value={supplierFilter}
            onChange={e => setSupplierFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-blue-400
                       focus:border-transparent bg-white"
          >
            <option value="all">כל הספקים</option>
            {suppliers.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>

        {/* Refresh */}
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 invisible">רענן</label>
          <button
            onClick={fetchBalances}
            disabled={loading}
            title="רענן נתונים"
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200
                       text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50
                       transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            רענן
          </button>
        </div>
      </div>

      {/* ── Error banner ────────────────────────────────────────────────────── */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200
                        rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {/* ── Summary cards ──────────────────────────────────────────────────── */}
      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4 flex items-center gap-4">
            <div className="p-2.5 bg-blue-50 rounded-lg">
              <Hash className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">סה&quot;כ מק&quot;טים</p>
              <p className="text-xl font-bold text-gray-900">{totalSkus.toLocaleString('he-IL')}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4 flex items-center gap-4">
            <div className="p-2.5 bg-emerald-50 rounded-lg">
              <Package className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">סה&quot;כ יתרת מלאי</p>
              <p className={`text-xl font-bold ${totalQty < 0 ? 'text-red-600' : 'text-gray-900'}`}>{formatQty(totalQty)}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4 flex items-center gap-4">
            <div className="p-2.5 bg-amber-50 rounded-lg">
              <TrendingUp className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">סה&quot;כ שווי מלאי</p>
              <p className={`text-xl font-bold ${totalValue < 0 ? 'text-red-600' : 'text-gray-900'}`}>{formatCurrency(totalValue)}</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Table card ──────────────────────────────────────────────────────── */}
      <div ref={tableRef} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">טוען יתרות…</span>
          </div>
        )}

        {/* Empty */}
        {!loading && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
            <PackageOpen className="w-10 h-10 opacity-40" />
            <p className="text-sm font-medium">
              {rows.length === 0
                ? 'אין תנועות מלאי מוגדרות עד לתאריך הנבחר'
                : 'אין תוצאות התואמות את הסינון'}
            </p>
            <p className="text-xs text-gray-400">
              {rows.length === 0
                ? 'הפק תעודות כניסה / יציאה כדי לראות יתרות כאן'
                : 'נסה לשנות את קריטריוני הסינון'}
            </p>
          </div>
        )}

        {/* Data table */}
        {!loading && filtered.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-gray-500
                               text-xs font-medium">
                  <th className="text-right px-4 py-3 w-14">תמונה</th>
                  <SortableTh label="מקט" sortKey="sku" activeKey={sort.key} dir={sort.dir} onSort={toggle} />
                  <SortableTh label="שם מוצר" sortKey="product_name" activeKey={sort.key} dir={sort.dir} onSort={toggle} />
                  <SortableTh label="מחסן" sortKey="warehouse_name" activeKey={sort.key} dir={sort.dir} onSort={toggle} />
                  <SortableTh label="ספק" sortKey="supplier_name" activeKey={sort.key} dir={sort.dir} onSort={toggle} />
                  <SortableTh label="יתרה" sortKey="calculated_quantity" activeKey={sort.key} dir={sort.dir} onSort={toggle} align="left" />
                  <SortableTh label="שווי מלאי" sortKey="total_value" activeKey={sort.key} dir={sort.dir} onSort={toggle} align="left" />
                  <SortableTh label="ספירה אחרונה" sortKey="last_count" activeKey={sort.key} dir={sort.dir} onSort={toggle} align="center" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, idx) => {
                  const qty        = row.calculated_quantity;
                  const val        = row.total_value ?? 0;
                  const isNegative = qty < 0;
                  const isZero     = qty === 0;
                  return (
                    <tr
                      key={`${row.product_id}-${row.warehouse_id}`}
                      className={`border-b border-gray-50 hover:bg-gray-50/50 transition-colors
                                  ${idx % 2 === 0 ? '' : 'bg-gray-50/30'}`}
                    >
                      {/* Image */}
                      <td className="px-4 py-3">
                        {row.image_url ? (
                          <img
                            src={row.image_url}
                            alt={row.product_name}
                            className="w-9 h-9 rounded-lg object-cover border border-gray-100"
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center
                                          justify-center">
                            <Boxes className="w-4 h-4 text-gray-300" />
                          </div>
                        )}
                      </td>

                      {/* SKU */}
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">
                        {row.sku ?? '—'}
                      </td>

                      {/* Product name */}
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {row.product_name}
                      </td>

                      {/* Warehouse */}
                      <td className="px-4 py-3 text-gray-600">
                        <span className="inline-flex items-center gap-1">
                          <Warehouse className="w-3 h-3 text-gray-400" />
                          {row.warehouse_name}
                        </span>
                      </td>

                      {/* Supplier */}
                      <td className="px-4 py-3 text-gray-600">
                        {row.supplier_id && supplierMap[row.supplier_id] ? (
                          <span className="inline-flex items-center gap-1">
                            <Truck className="w-3 h-3 text-gray-400" />
                            {supplierMap[row.supplier_id]}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>

                      {/* Balance quantity */}
                      <td className="px-4 py-3 text-left">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold
                            ${isNegative
                              ? 'bg-red-100 text-red-700'
                              : isZero
                                ? 'bg-gray-100 text-gray-500'
                                : 'bg-emerald-100 text-emerald-700'}`}
                        >
                          {formatQty(qty)}
                        </span>
                      </td>

                      {/* Inventory value */}
                      <td className="px-4 py-3 text-left font-medium text-gray-700">
                        {val === 0 ? (
                          <span className="text-gray-300">—</span>
                        ) : (
                          <span className={val < 0 ? 'text-red-600' : 'text-gray-800'}>
                            {formatCurrency(val)}
                          </span>
                        )}
                      </td>

                      {/* Last stock count date */}
                      <td className="px-4 py-3 text-center text-xs">
                        {(() => {
                          const lcd = lastCountDates[`${row.product_id}::${row.warehouse_id}`];
                          return lcd
                            ? <span className="text-gray-600">{new Date(lcd).toLocaleDateString('he-IL')}</span>
                            : <span className="text-gray-300">—</span>;
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-gray-100/80 border-t-2 border-gray-200">
                  <td className="px-4 py-3" />
                  <td className="px-4 py-3" />
                  <td className="px-4 py-3 font-bold text-gray-800 text-sm">
                    סה&quot;כ ({totalSkus} מק&quot;טים)
                  </td>
                  <td className="px-4 py-3" />
                  <td className="px-4 py-3" />
                  <td className="px-4 py-3 text-left">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-bold ${totalQty < 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {formatQty(totalQty)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-left font-bold text-gray-800">
                    <span className={totalValue < 0 ? 'text-red-600' : 'text-gray-900'}>
                      {formatCurrency(totalValue)}
                    </span>
                  </td>
                  <td className="px-4 py-3" />
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* ── Footer summary ─────────────────────────────────────────────────── */}
        {!loading && filtered.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/60
                          flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-gray-500">

            {/* Row count */}
            <span>
              {filtered.length} שורות
              {filtered.length !== rows.length && ` (מתוך ${rows.length} סה"כ)`}
            </span>

            {/* Valuation method badge */}
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                             bg-blue-50 text-blue-600 font-medium">
              {VALUATION_OPTIONS.find(o => o.value === valuation)?.label ?? valuation}
            </span>

            {/* Spacer */}
            <span className="flex-1" />

            {/* As-of date */}
            <span>
              נכון לתאריך:{' '}
              <span className="font-medium text-gray-700">
                {new Date(asOfDate + 'T00:00:00').toLocaleDateString('he-IL')}
              </span>
            </span>

            {/* Total SKUs */}
            <span className="flex items-center gap-1 font-semibold text-gray-800
                             border-r border-gray-200 pr-4">
              <Hash className="w-3.5 h-3.5 text-blue-500" />
              סה&quot;כ מק&quot;טים:&nbsp;
              <span className="text-blue-700">{totalSkus.toLocaleString('he-IL')}</span>
            </span>

            {/* Total quantity */}
            <span className="flex items-center gap-1 font-semibold text-gray-800
                             border-r border-gray-200 pr-4">
              <Package className="w-3.5 h-3.5 text-emerald-500" />
              סה&quot;כ יתרה:&nbsp;
              <span className={totalQty < 0 ? 'text-red-600' : 'text-emerald-700'}>{formatQty(totalQty)}</span>
            </span>

            {/* Total value */}
            <span className="flex items-center gap-1 font-semibold text-gray-800
                             border-r border-gray-200 pr-4">
              <TrendingUp className="w-3.5 h-3.5 text-amber-500" />
              סה&quot;כ שווי מלאי:&nbsp;
              <span className={totalValue < 0 ? 'text-red-600' : 'text-amber-700'}>
                {formatCurrency(totalValue)}
              </span>
            </span>
          </div>
        )}
      </div>

      {sendTarget && companyId && (
        <SendConsignmentReportModal
          companyId={companyId}
          clientId={sendTarget.store.id}
          clientName={sendTarget.store.name}
          initialAsOf={sendTarget.asOf}
          onChangeStore={() => setPickerOpen(true)}
          onClose={() => setSendTarget(null)}
          onSent={loadSendInfo}
        />
      )}

      {pickerOpen && (
        <ConsignmentStorePickerModal
          stores={stores}
          lastSent={lastSent}
          selectedId={sendTarget?.store.id}
          onClose={() => setPickerOpen(false)}
          onPick={pickStore}
        />
      )}
    </div>
  );
}
